import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import type { PayloadAction } from '@reduxjs/toolkit';
import { fetchShopSettings, updateShopSettings, getShopLanguageSettings } from '../../api/setting/setting';
import type { UpdateSettingParams, GetShopLanguageSettingsParams } from '../../api/setting/setting';

// 定义设置项的接口
export interface SettingItem {
  itemId: number;
  itemName: string;
  itemValue: string;
  langCode: string;
}

// 定义设置状态的接口
export interface SettingState {
  // 原始数据
  originalSettings: {
    [key: string]: SettingItem;
  };
  // 修改后的数据
  modifiedSettings: {
    [key: string]: SettingItem;
  };
  // 变更记录
  changeLog: ChangeLogItem[];
  // 是否有未保存的更改
  hasUnsavedChanges: boolean;
}

// 设置变更记录类型定义
export interface ChangeLogItem {
  itemName: string;
  oldValue: string;
  newValue: string;
  timestamp: number;
}

// 更新设置的载荷类型
export interface UpdateSettingPayload {
  itemName: string;
  value: string | number | boolean | object;
  isInitialLoad?: boolean; // 添加初始化标志
}

// 初始化状态
const initialState: SettingState = {
  originalSettings: {}, // 使用空对象，将在获取数据后设置
  modifiedSettings: {}, // 使用空对象，将在获取数据后设置
  changeLog: [],
  hasUnsavedChanges: false,
};

// 辅助函数：标准化 JSON 字符串格式
const normalizeJsonString = (jsonStr: string): string => {
  try {
    return JSON.stringify(JSON.parse(jsonStr));
  } catch (e) {
    return jsonStr;
  }
};

// 辅助函数：比较两个设置值是否相等
const compareSettings = (original: SettingItem, modified: SettingItem): boolean => {
  if (!original || !modified) return false;
  
  let originalValue = original.itemValue;
  let modifiedValue = modified.itemValue;
  
  // 处理 JSON 字符串格式
  if (typeof originalValue === 'string' && originalValue.startsWith('{') && originalValue.endsWith('}')) {
    originalValue = normalizeJsonString(originalValue);
  }
  
  if (typeof modifiedValue === 'string' && modifiedValue.startsWith('{') && modifiedValue.endsWith('}')) {
    modifiedValue = normalizeJsonString(modifiedValue);
  }
  
  // 处理非字符串值
  if (typeof originalValue !== 'string' && typeof modifiedValue === 'string') {
    return JSON.stringify(originalValue) === modifiedValue;
  }
  
  if (typeof originalValue === 'string' && typeof modifiedValue !== 'string') {
    return originalValue === JSON.stringify(modifiedValue);
  }
  
  if (typeof originalValue !== 'string' && typeof modifiedValue !== 'string') {
    return JSON.stringify(originalValue) === JSON.stringify(modifiedValue);
  }
  
  return originalValue === modifiedValue;
};

// 添加异步thunk：获取设置
export const fetchSettings = createAsyncThunk(
  'chatSetting/fetchSettings',
  async (shopDomain: string, { rejectWithValue }) => {
    try {
      const response = await fetchShopSettings(shopDomain);
      return response;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : '获取设置失败');
    }
  }
);

// 添加异步thunk：保存设置
export const saveSettings = createAsyncThunk(
  'chatSetting/saveSettings',
  async (params: UpdateSettingParams, { rejectWithValue }) => {
    try {
      const response = await updateShopSettings(params);
      return response;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : '保存设置失败');
    }
  }
);

// 添加异步thunk：获取店铺多语言设置
export const fetchLanguageSettings = createAsyncThunk(
  'chatSetting/fetchLanguageSettings',
  async (params: GetShopLanguageSettingsParams, { rejectWithValue }) => {
    try {
      const response = await getShopLanguageSettings(params);
      return response;
    } catch (error) {
      return rejectWithValue(error instanceof Error ? error.message : '获取多语言设置失败');
    }
  }
);

// 创建 slice
const chatSettingSlice = createSlice({
  name: 'chatSetting',
  initialState,
  reducers: {
    // 设置原始数据（从服务端获取）
    setOriginalSettings: (state, action: PayloadAction<Record<string, SettingItem>>) => {
      // 设置原始数据
      state.originalSettings = action.payload;
      // 设置修改后的数据与原始数据相同
      state.modifiedSettings = JSON.parse(JSON.stringify(action.payload));
      // 清空变更记录
      state.changeLog = [];
      // 标记没有未保存的更改
      state.hasUnsavedChanges = false;
    },
    
    // 更新设置项
    updateSetting: (state, action: PayloadAction<UpdateSettingPayload>) => {
      const { itemName, value, isInitialLoad = false } = action.payload;
      
      // 确保 modifiedSettings 中存在该设置项
      if (!state.modifiedSettings[itemName]) {
        if (state.originalSettings[itemName]) {
          // 如果原始设置中存在，则复制一份
          state.modifiedSettings[itemName] = { ...state.originalSettings[itemName] };
        } else {
          // 如果原始设置中不存在，则创建一个新的
          state.modifiedSettings[itemName] = {
            itemId: 0, // 新项的 ID 将在保存时由服务器分配
            itemName,
            itemValue: '',
            langCode: 'en' // 默认语言
          };
        }
      }
      
      // 获取旧值
      const oldValue = state.modifiedSettings[itemName].itemValue;
      
      // 更新修改后的设置值
      state.modifiedSettings[itemName].itemValue = typeof value === 'string' ? value : JSON.stringify(value);
      
      // 如果是初始加载，则不触发hasUnsavedChanges的变化
      if (isInitialLoad) {
        return;
      }
      
      // 检查是否与原始设置不同
      const original = state.originalSettings[itemName];
      const modified = state.modifiedSettings[itemName];
      
      // 使用 compareSettings 函数检查是否有变更
      const hasChanged = !compareSettings(original, modified);
      
      // 如果有变更且变更日志中不存在该项，则添加到变更日志
      if (hasChanged) {
        const existingLogIndex = state.changeLog.findIndex(log => { return log.itemName === itemName });
        
        // 将值转换为字符串以便显示
        const oldValueStr = typeof oldValue === 'object' ? JSON.stringify(oldValue) : String(oldValue);
        const newValueStr = typeof value === 'object' ? JSON.stringify(value) : String(value);
        
        if (existingLogIndex === -1) {
          state.changeLog.push({
            itemName,
            oldValue: oldValueStr,
            newValue: newValueStr,
            timestamp: Date.now()
          });
        } else {
          // 更新现有日志的时间戳和新值
          state.changeLog[existingLogIndex].timestamp = Date.now();
          state.changeLog[existingLogIndex].newValue = newValueStr;
        }
        
        // 标记有未保存的变更
        state.hasUnsavedChanges = true;
      } else {
        // 如果没有变更，则从变更日志中移除该项
        state.changeLog = state.changeLog.filter(log => { return log.itemName !== itemName });
        
        // 检查是否还有其他未保存的变更
        state.hasUnsavedChanges = state.changeLog.length > 0;
      }
    },
    
    // 保存更改
    saveChanges: (state) => {
      // 将修改后的数据设置为原始数据
      state.originalSettings = JSON.parse(JSON.stringify(state.modifiedSettings));
      // 清空变更记录
      state.changeLog = [];
      // 标记没有未保存的更改
      state.hasUnsavedChanges = false;
    },
    
    // 取消更改
    cancelChanges: (state) => {
      // 将原始数据设置为修改后的数据
      state.modifiedSettings = JSON.parse(JSON.stringify(state.originalSettings));
      // 清空变更记录
      state.changeLog = [];
      // 标记没有未保存的更改
      state.hasUnsavedChanges = false;
    },
  },
  extraReducers: (builder) => {
    // 处理获取设置的异步操作
    builder
      .addCase(fetchSettings.pending, (state) => {
        // 可以添加加载状态
        // state.loading = true;
      })
      .addCase(fetchSettings.fulfilled, (state, action) => {
        // 设置获取到的数据
        if (action.payload?.settings) {
          // 设置原始数据
          state.originalSettings = action.payload.settings;
          // 设置修改后的数据与原始数据相同
          state.modifiedSettings = JSON.parse(JSON.stringify(action.payload.settings));
          // 清空变更记录
          state.changeLog = [];
          // 标记没有未保存的更改
          state.hasUnsavedChanges = false;
        }
        // state.loading = false;
      })
      .addCase(fetchSettings.rejected, (state, action) => {
        // 可以添加错误处理
        // state.error = action.payload as string;
        // state.loading = false;
      })
      
      // 处理保存设置的异步操作
      .addCase(saveSettings.pending, (state) => {
        // 可以添加加载状态
        // state.loading = true;
      })
      .addCase(saveSettings.fulfilled, (state, action) => {
        // 保存成功后的处理
        // state.loading = false;
      })
      .addCase(saveSettings.rejected, (state, action) => {
        // 可以添加错误处理
        // state.error = action.payload as string;
        // state.loading = false;
      })
      
      // 处理获取多语言设置的异步操作
      .addCase(fetchLanguageSettings.pending, (state) => {
        // 可以添加加载状态
        // state.loading = true;
      })
      .addCase(fetchLanguageSettings.fulfilled, (state, action) => {
        if (action.payload?.settings) {
          // 从API响应中获取设置
          const translationSettings = action.payload.settings;
          
          // 检查是否有originSettings参数
          const originSettings = action.meta?.arg?.originSettings;
          
          // 先更新 originalSettings
          if (originSettings) {
            // 如果提供了originSettings,使用它作为基础
            state.originalSettings = {
              ...originSettings,  // 保持原有设置
              ...translationSettings  // 只替换API返回的翻译字段
            };
          } else {
            // 如果没有提供originSettings,则保持原有设置,只更新API返回的翻译字段
            state.originalSettings = {
              ...state.originalSettings,
              ...translationSettings
            };
          }
          
          // 更新 modifiedSettings 以匹配新的 originalSettings
          state.modifiedSettings = {
            ...state.modifiedSettings,  // 保持其他未修改的设置
            ...translationSettings  // 使用新的翻译设置覆盖
          };
          
          // 清空变更记录
          state.changeLog = [];
          
          // 标记没有未保存的更改
          state.hasUnsavedChanges = false;
          console.log('state.originalSettings', state.originalSettings, state.modifiedSettings, state.changeLog, translationSettings);
        }
        // state.loading = false;
      })
      .addCase(fetchLanguageSettings.rejected, (state, action) => {
        // 可以添加错误处理
        // state.error = action.payload as string;
        // state.loading = false;
      });
  },
});

// 导出 actions
export const { setOriginalSettings, updateSetting, saveChanges, cancelChanges } = chatSettingSlice.actions;

// 导出选择器函数
export const selectOriginalSettings = (state: { chatSetting: SettingState }) => state.chatSetting.originalSettings;
export const selectModifiedSettings = (state: { chatSetting: SettingState }) => state.chatSetting.modifiedSettings;
export const selectHasUnsavedChanges = (state: { chatSetting: SettingState }) => state.chatSetting.hasUnsavedChanges;
export const selectChangeLog = (state: { chatSetting: SettingState }) => state.chatSetting.changeLog;

// 导出 reducer
export default chatSettingSlice.reducer; 